import HundredBricksAPI from '@lib/hundredBricksAPI/HundredBricksAPI';
import { APIError } from '@lib/api';
import CreateInvestorGoalError from './createInvestorGoal';
import { InvestorGoalEntity } from './InvestorGoalEntity';
import { PreviewGoal, PreviewGoalError } from './previewGoals';
import { AccountValueMustBeANumber, AccountValueMustNotBeNegative, InvestorGoalNotFound, InvestorGoalError, MissingAccountValue } from './investorGoal';
import UpdateInvestorGoalError from './updateInvestorGoal';
import { GoalDoesNotBelongToTheInvestor, GoalDurationIsBelowMinium, GoalIsAlreadyDeactivated, GoalNotFound, InvalidGoalId, AmountBelowLastMonthRentIncome, AmountBelowPatrimonyAccountValue, InvalidGoalPlanType, InvalidGoalType, InvesmentAmountIsBelowMinium, MonthlyInvestmentCapacityMustBeAnInteger, MonthlyInvestmentCapacityMustBeMoreThanZero, ProjectedTimeExceedsLimit, TimeToGoalMustBeMoreThanZero } from './GoalError';
import DeactivateInvestorGoalError from './deactivateInvestorGoal';
class GoalsAPI extends HundredBricksAPI {
    async investorGoal() {
        try {
            const { data } = await this.client.get('v2/goals');
            return new InvestorGoalEntity(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'GOAL_NOT_FOUND':
                        throw new InvestorGoalNotFound({ cause: error });
                    case 'ACCOUNT_VALUE_MUST_BE_A_NUMBER':
                        throw new AccountValueMustBeANumber({ cause: error });
                    case 'ACCOUNT_VALUE_MUST_NOT_BE_NEGATIVE':
                        throw new AccountValueMustNotBeNegative({ cause: error });
                    case 'MISSING_ACCOUNT_VALUE':
                        throw new MissingAccountValue({ cause: error });
                    default:
                        throw new InvestorGoalError({ cause: error });
                }
            }
            throw new InvestorGoalError(`Unexpected error when obtaining investor goal: '${error}'`);
        }
    }
    async previewGoal(params) {
        try {
            const { data } = await this.client.post('v2/goals/preview', params);
            return new PreviewGoal(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'AMOUNT_MUST_BE_MORE_THAN_ACCOUNT_VALUE_IN_PATRIMONY_TYPE':
                        throw new AmountBelowPatrimonyAccountValue({ cause: error });
                    case 'AMOUNT_MUST_BE_MORE_THAN_LAST_MONTH_RENT_INCOME_IN_RENTS_TYPE':
                        throw new AmountBelowLastMonthRentIncome({ cause: error });
                    case 'INVALID_GOAL_TYPE':
                        throw new InvalidGoalType({ cause: error });
                    case 'INVALID_GOAL_PLAN_TYPE':
                        throw new InvalidGoalPlanType({ cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_MORE_THAN_ZERO':
                        throw new MonthlyInvestmentCapacityMustBeMoreThanZero({ cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_AN_INTEGER':
                        throw new MonthlyInvestmentCapacityMustBeAnInteger({ cause: error });
                    case 'TIME_TO_GOAL_MUST_BE_MORE_THAN_ZERO':
                        throw new TimeToGoalMustBeMoreThanZero({ cause: error });
                    case 'PROJECTED_TIME_EXCEEDS_LIMIT':
                        throw new ProjectedTimeExceedsLimit({ cause: error });
                    case 'GOAL_DURATION_IS_BELOW_MINIMUM':
                        throw new GoalDurationIsBelowMinium({ cause: error });
                    case 'INVESTMENT_AMOUNT_IS_BELOW_MINIMUM':
                        throw new InvesmentAmountIsBelowMinium({ cause: error });
                    default:
                        throw new PreviewGoalError({ cause: error });
                }
            }
            throw new PreviewGoalError(`Unexpected error when obtaining preview goal: '${error}'`);
        }
    }
    async createInvestorGoal(payload) {
        try {
            const { data } = await this.client.post('v2/goals', payload);
            return new InvestorGoalEntity(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'AMOUNT_MUST_BE_MORE_THAN_ACCOUNT_VALUE_IN_PATRIMONY_TYPE':
                        throw new AmountBelowPatrimonyAccountValue({ cause: error });
                    case 'AMOUNT_MUST_BE_MORE_THAN_LAST_MONTH_RENT_INCOME_IN_RENTS_TYPE':
                        throw new AmountBelowLastMonthRentIncome({ cause: error });
                    case 'INVALID_GOAL_PLAN_TYPE':
                        throw new InvalidGoalPlanType({ cause: error });
                    case 'INVALID_GOAL_TYPE':
                        throw new InvalidGoalType({ cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_AN_INTEGER':
                        throw new MonthlyInvestmentCapacityMustBeAnInteger({ cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_MORE_THAN_ZERO':
                        throw new MonthlyInvestmentCapacityMustBeMoreThanZero({ cause: error });
                    case 'PROJECTED_TIME_EXCEEDS_LIMIT':
                        throw new ProjectedTimeExceedsLimit({ cause: error });
                    case 'TIME_TO_GOAL_MUST_BE_MORE_THAN_ZERO':
                        throw new TimeToGoalMustBeMoreThanZero({ cause: error });
                    case 'GOAL_DURATION_IS_BELOW_MINIMUM':
                        throw new GoalDurationIsBelowMinium({ cause: error });
                    case 'INVESTMENT_AMOUNT_IS_BELOW_MINIMUM':
                        throw new InvesmentAmountIsBelowMinium({ cause: error });
                    default:
                        throw new CreateInvestorGoalError({ cause: error });
                }
            }
            throw new CreateInvestorGoalError(`Unexpected error when create investor goal: '${error}'`);
        }
    }
    async updateInvestorGoal(goalId, payload) {
        try {
            const { data } = await this.client.put(`v2/goals/${goalId}`, payload);
            return new InvestorGoalEntity(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVALID_GOAL_ID':
                        throw new InvalidGoalId(goalId, { cause: error });
                    case 'AMOUNT_MUST_BE_MORE_THAN_ACCOUNT_VALUE_IN_PATRIMONY_TYPE':
                        throw new AmountBelowPatrimonyAccountValue({ cause: error });
                    case 'AMOUNT_MUST_BE_MORE_THAN_LAST_MONTH_RENT_INCOME_IN_RENTS_TYPE':
                        throw new AmountBelowLastMonthRentIncome({ cause: error });
                    case 'INVALID_GOAL_TYPE':
                        throw new InvalidGoalType({ cause: error });
                    case 'INVALID_GOAL_PLAN_TYPE':
                        throw new InvalidGoalPlanType({ cause: error });
                    case 'GOAL_DOES_NOT_BELONG_TO_THE_INVESTOR':
                        throw new GoalDoesNotBelongToTheInvestor({ cause: error });
                    case 'GOAL_NOT_FOUND':
                        throw new GoalNotFound(goalId, { cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_MORE_THAN_ZERO':
                        throw new MonthlyInvestmentCapacityMustBeMoreThanZero({ cause: error });
                    case 'MONTHLY_INVESTMENT_CAPACITY_MUST_BE_AN_INTEGER':
                        throw new MonthlyInvestmentCapacityMustBeAnInteger({ cause: error });
                    case 'TIME_TO_GOAL_MUST_BE_MORE_THAN_ZERO':
                        throw new TimeToGoalMustBeMoreThanZero({ cause: error });
                    case 'GOAL_IS_ALREADY_DEACTIVATED':
                        throw new GoalIsAlreadyDeactivated(goalId, { cause: error });
                    case 'PROJECTED_TIME_EXCEEDS_LIMIT':
                        throw new ProjectedTimeExceedsLimit({ cause: error });
                    case 'GOAL_DURATION_IS_BELOW_MINIMUM':
                        throw new GoalDurationIsBelowMinium({ cause: error });
                    case 'INVESTMENT_AMOUNT_IS_BELOW_MINIMUM':
                        throw new InvesmentAmountIsBelowMinium({ cause: error });
                    default:
                        throw new UpdateInvestorGoalError({ cause: error });
                }
            }
            throw new UpdateInvestorGoalError(`Unexpected error when update investor goal: '${error}'`);
        }
    }
    async deactivateInvestorGoal(goalId) {
        try {
            const { data } = await this.client.post(`v2/goals/${goalId}/deactivate`);
            return new InvestorGoalEntity(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'INVALID_GOAL_ID':
                        throw new InvalidGoalId(goalId, { cause: error });
                    case 'GOAL_DOES_NOT_BELONG_TO_THE_INVESTOR':
                        throw new GoalDoesNotBelongToTheInvestor({ cause: error });
                    case 'GOAL_NOT_FOUND':
                        throw new GoalNotFound(goalId, { cause: error });
                    case 'GOAL_IS_ALREADY_DEACTIVATED':
                        throw new GoalIsAlreadyDeactivated(goalId, { cause: error });
                    default:
                        throw new DeactivateInvestorGoalError({ cause: error });
                }
            }
            throw new DeactivateInvestorGoalError(`Unexpected error when deactivate investor goal: '${error}'`);
        }
    }
}
export default GoalsAPI;
